/**
 * Toast on an open work order when its Svea payment link is paid (#3947).
 *
 * The server announces the payment as a staff notification (`notification:new`).
 * When it is about the work order on screen, show it and refresh the payment
 * views: a payment recorded by the reconciliation worker sends no
 * `entity:changed`, so this refresh is the only one that page gets.
 */

import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '@/hooks/queryKeys';
import { useWebSocket } from '@/hooks/useWebSocket';
import {
  staffNotificationMessageKey,
  staffNotificationParams,
  type StaffNotification,
} from '@/hooks/useStaffNotifications';
import { useNotify } from '@/stores/notificationStore';

export const SVEA_PAYMENT_RECEIVED_TYPE = 'svea_payment_received';

function isPaymentForWorkOrder(payload: unknown, workOrderId: string): payload is StaffNotification {
  if (payload == null || typeof payload !== 'object') return false;
  const notification = payload as Partial<StaffNotification>;
  return (
    notification.type === SVEA_PAYMENT_RECEIVED_TYPE &&
    notification.entityType === 'work_order' &&
    notification.entityId === workOrderId &&
    typeof notification.titleKey === 'string'
  );
}

export function useSveaPaymentReceivedToast(workOrderId: string): void {
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();

  const onNotification = useCallback(
    (payload: unknown) => {
      if (!isPaymentForWorkOrder(payload, workOrderId)) return;
      const params = staffNotificationParams(payload.params ?? {});
      notify.success(
        tc(staffNotificationMessageKey(payload.titleKey), params),
        payload.bodyKey ? tc(staffNotificationMessageKey(payload.bodyKey), params) : undefined,
      );
      for (const queryKey of [
        queryKeys.workOrders.detail(workOrderId),
        queryKeys.invoicePreview(workOrderId),
        queryKeys.invoices.all,
        ['workOrderInvoices', workOrderId],
      ]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
    [workOrderId, notify, tc, queryClient],
  );

  useWebSocket('notification:new', onNotification);
}
